import { useState, useRef, useEffect } from "react";
import { FiEdit2, FiChevronDown } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "../../utils/api";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { formatMessageTime, getInitials } from "../../utils/helpers";
import LinkPreview, { extractUrls } from "./LinkPreview";
import VoiceMessage from "./VoiceMessage";
import ForwardModal from "./ForwardModal";
import EmojiPicker from "./EmojiPicker";
import ImageLightbox from "./ImageLightbox";
import FileBubble from "./FileBubble";
import MemberProfilePopup from "./MemberProfilePopup";
import ReactionDetailModal from "./ReactionDetailModal";
import styles from "./MessageBubble.module.css";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// SVG icons exactly matching WhatsApp style
const IconReply = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 17 4 12 9 7" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </svg>
);
const IconCopy = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const IconForward = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 17 20 12 15 7" />
    <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
  </svg>
);
const IconPin = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);
const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IconDelete = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);


// WhatsApp-style tick component
// 1 grey tick = sent, 2 grey ticks = delivered, 2 blue ticks = seen
const Ticks = ({ readBy = [] }) => {
  // readBy[0] is always the sender themselves
  // readBy.length > 1 means at least one recipient has read it
  const seen = readBy.length > 1;

  return (
    <svg width="18" height="11" viewBox="0 0 18 11" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* First tick (always shown) */}
      <path
        d="M1 5.5L4.5 9L11 2"
        stroke={seen ? "#60b4f6" : "rgba(255,255,255,0.6)"}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Second tick (shown when delivered/seen) */}
      <path
        d="M6 5.5L9.5 9L16 2"
        stroke={seen ? "#60b4f6" : "rgba(255,255,255,0.6)"}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const MessageBubble = ({ message, isOwn, showAvatar, conversationId, onReply }) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editText, setEditText] = useState(message.text);
  const [showForward, setShowForward] = useState(false);
  const [showMoreEmojis, setShowMoreEmojis] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [memberPopup, setMemberPopup] = useState(null); // { member, rect }
  const [showReactionDetail, setShowReactionDetail] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const longPressTimer = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const { user } = useAuthStore();
  const { deleteMessageFromState, togglePinMessage, updateMessageInState, reactToMessage, activeConversation } = useChatStore();
  const isPinned = (activeConversation?.pinnedMessages || []).some(
    (m) => (m._id || m) === message._id
  );
  const socketRef = useSocket();
  const urls = extractUrls(message.text || "");

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const close = () => setMenuOpen(false);

  const handleReact = async (emoji) => {
    close();
    try {
      const result = await reactToMessage(message._id, emoji);
      socketRef?.current?.emit("message:react", {
        conversationId,
        messageId: result.messageId,
        reactions: result.reactions,
      });
    } catch (_) {}
  };

  const handleReply = () => { close(); onReply(message); };

  const handleCopy = () => {
    close();
    navigator.clipboard.writeText(message.text);
    toast.success("Copied!");
  };

  const handleForward = () => { close(); setShowForward(true); };

  const handlePin = async () => {
    close();
    try {
      const result = await togglePinMessage(conversationId, message._id);
      socketRef?.current?.emit("message:pin", { conversationId, pinnedMessages: result.pinnedMessages });
      toast.success(result.isPinned ? "Message pinned 📌" : "Message unpinned");
    } catch { toast.error("Failed to pin message"); }
  };

  const handleEdit = () => { close(); setIsEditing(true); };

  const handleEditSave = async () => {
    if (!editText.trim() || editText === message.text) { setIsEditing(false); return; }
    try {
      await api.put(`/messages/${message._id}`, { text: editText });
      updateMessageInState(message._id, { text: editText, edited: true });
      socketRef?.current?.emit("message:edit", {
        conversationId,
        messageId: message._id,
        text: editText,
      });
      setIsEditing(false);
      toast.success("Message updated");
    } catch { toast.error("Failed to edit"); }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleEditSave(); }
    if (e.key === "Escape") { setIsEditing(false); setEditText(message.text); }
  };

  const handleDelete = () => {
    close();
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (deleteForEveryone) => {
    setShowDeleteModal(false);
    try {
      const res = await api.delete(`/messages/${message._id}`, { data: { deleteForEveryone } });
      deleteMessageFromState(message._id, deleteForEveryone);
      socketRef?.current?.emit("message:delete", { 
        conversationId, 
        messageId: message._id,
        isDeletedForEveryone: deleteForEveryone,
      });
    } catch { toast.error("Failed to delete message"); }
  };

  // Group reactions by emoji for display
  const grouped = (message.reactions || []).reduce((acc, r) => {
    acc[r.emoji] = (acc[r.emoji] || 0) + 1;
    return acc;
  }, {});

  // System messages render centered like WhatsApp
  if (message.isSystem) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        padding: "4px 16px",
        margin: "2px 0",
      }}>
        <span style={{
          background: "rgba(0,0,0,0.2)",
          color: "var(--text-secondary)",
          fontSize: "11.5px",
          padding: "4px 12px",
          borderRadius: "12px",
          textAlign: "center",
          backdropFilter: "blur(4px)",
          maxWidth: "80%",
        }}>
          {message.text}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${styles.wrapper} ${isOwn ? styles.own : styles.other}`}
      style={swipeOffset !== 0 ? { transform: `translateX(${swipeOffset}px)`, transition: swipeOffset === 0 ? "transform 0.3s" : "none" } : {}}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); }}
      data-id={message._id}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
        longPressTimer.current = setTimeout(() => {
          if (navigator.vibrate) navigator.vibrate(30);
          setMenuOpen(true);
        }, 500);
      }}
      onTouchMove={(e) => {
        clearTimeout(longPressTimer.current);
        const dx = e.touches[0].clientX - touchStartX.current;
        const dy = Math.abs(e.touches[0].clientY - touchStartY.current);
        if (dy < 20 && dx > 10 && dx < 80) setSwipeOffset(dx);
      }}
      onTouchEnd={() => {
        clearTimeout(longPressTimer.current);
        if (swipeOffset > 40) { onReply(message); if (navigator.vibrate) navigator.vibrate(20); }
        setSwipeOffset(0);
      }}
    >
      {/* Avatar */}
      {!isOwn && showAvatar && (
        <div
          className="avatar"
          style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0, alignSelf: "flex-end", cursor: "pointer" }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setMemberPopup({ member: message.sender, rect });
          }}
          title={message.sender?.name}
        >
          {message.sender?.avatar
            ? <img src={message.sender.avatar} alt={message.sender?.name}  />
            : getInitials(message.sender?.name || "")}
        </div>
      )}
      {!isOwn && !showAvatar && <div style={{ width: 28, flexShrink: 0 }} />}

      {/* Message content */}
      <div className={styles.content}>
        {!isOwn && showAvatar && (
          <span
            className={styles.senderName}
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setMemberPopup({ member: message.sender, rect });
            }}
          >
            {message.sender?.name}
          </span>
        )}

        {message.isForwarded && (
          <div className={`${styles.forwardedLabel} ${isOwn ? styles.forwardedOwn : ""}`}>
            <IconForward />
            <span>Forwarded</span>
          </div>
        )}

        {/* Reply quote */}
        {message.replyTo && (
          <div className={`${styles.replyQuote} ${isOwn ? styles.replyQuoteOwn : ""}`}>
            <span className={styles.replyQuoteName}>{message.replyTo.sender?.name}</span>
            <span className={styles.replyQuoteText}>
              {message.replyTo.image && !message.replyTo.text ? "📷 Photo" : message.replyTo.text?.slice(0, 80)}
            </span>
          </div>
        )}

        {/* Status reply preview — shows the status snapshot above the message */}
        {message.statusReply?.userName && (
          <div className={`${styles.statusReplyPreview} ${isOwn ? styles.statusReplyOwn : styles.statusReplyOther}`}>
            {message.statusReply.mediaType === "image" && message.statusReply.media && (
              <img src={message.statusReply.media} alt="status" className={styles.statusReplyImg} />
            )}
            {message.statusReply.mediaType === "video" && message.statusReply.media && (
              <div className={styles.statusReplyVideo}>🎬 Video status</div>
            )}
            {message.statusReply.mediaType === "text" && (
              <div
                className={styles.statusReplyText}
                style={{ background: message.statusReply.bgColor || "#7b7ff5" }}
              >
                <span>{message.statusReply.textContent}</span>
              </div>
            )}
            <span className={styles.statusReplyLabel}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.7}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Replied to {message.statusReply.userName}'s status
            </span>
          </div>
        )}

        {/* Bubble */}
        <div className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : styles.bubbleOther}`}>
          {message.image && (
            <img src={message.image} alt="attachment" className={styles.messageImage}
              onClick={() => setLightboxSrc(message.image)} />
          )}
          {message.gifUrl && (
            <img src={message.gifUrl} alt="GIF" className={styles.gifMessage}
              onClick={() => setLightboxSrc(message.gifUrl)} />
          )}
          {/* Deleted for everyone */}
          {message.isDeletedForEveryone ? (
            <p className={styles.deletedText}>
              {isOwn ? "🚫 You deleted this message" : "🚫 This message was deleted"}
            </p>
          ) : (
          <>
            {message.isVoice && (
              <VoiceMessage url={message.voiceUrl} duration={message.voiceDuration} isOwn={isOwn} />
            )}
            {/* File/document/video attachment */}
            {message.file && !message.isVoice && (
              message.fileType?.startsWith("video/") ? (
                <div className={styles.videoBubble}>
                  <video
                    src={message.file}
                    controls
                    className={styles.videoPlayer}
                    preload="metadata"
                  />
                  {message.fileName && (
                    <div className={styles.videoMeta}>{message.fileName}</div>
                  )}
                </div>
              ) : (
                <FileBubble
                  file={message.file}
                  fileName={message.fileName}
                  fileSize={message.fileSize}
                  fileType={message.fileType}
                  isOwn={isOwn}
                />
              )
            )}
            {isEditing ? (
              <textarea
                className={styles.editInput}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleEditKeyDown}
                autoFocus rows={2}
              />
            ) : (
              message.text && !message.isVoice && (
                <>
                  <p className={styles.text}>
                    {message.text}
                    {message.edited && <span className={styles.editedTag}> (edited)</span>}
                  </p>
                  {urls.length > 0 && <LinkPreview url={urls[0]} isOwn={isOwn} />}
                </>
              )
            )}
          </>
          )}

          {/* Meta inside bubble */}
          <div className={`${styles.meta} ${isOwn ? styles.metaOwn : ""}`}>
            <span className={styles.time} title={new Date(message.createdAt).toLocaleString()}>
              {formatMessageTime(message.createdAt)}
            </span>
            {isOwn && (
              <span className={styles.readIcon}>
                <Ticks readBy={message.readBy} />
              </span>
            )}
          </div>
        </div>

        {/* Reaction badges below bubble */}
        {Object.keys(grouped).length > 0 && (
          <div className={`${styles.reactionBadges} ${isOwn ? styles.reactionBadgesOwn : ""}`}>
            {Object.entries(grouped).map(([emoji, count]) => (
              <button
                key={emoji}
                className={styles.reactionBadge}
                onClick={() => setShowReactionDetail(true)}
                title="Click to see who reacted"
              >
                <span>{emoji}</span>
                <span className={styles.reactionCount}>{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Dropdown trigger — chevron appears on hover */}
      <div className={`${styles.menuTriggerWrap} ${isOwn ? styles.menuTriggerOwn : styles.menuTriggerOther}`}>
        {(hovered || menuOpen) && !message.isDeletedForEveryone && (
          <button
            ref={triggerRef}
            className={`${styles.menuTrigger} ${menuOpen ? styles.menuTriggerActive : ""}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen((p) => !p); }}
            title="More options"
          >
            <FiChevronDown size={15} />
          </button>
        )}

        {/* WhatsApp-style dropdown */}
        {menuOpen && (
          <div
            ref={menuRef}
            className={`${styles.menu} ${isOwn ? styles.menuOwn : styles.menuOther}`}
          >
            {/* Emoji reaction strip — WhatsApp style with selected highlight */}
            <div className={styles.emojiStrip}>
              {QUICK_EMOJIS.map((e) => {
                const myReaction = (message.reactions || []).find(
                  (r) => (r.user?._id || r.user) === user?._id
                );
                const isSelected = myReaction?.emoji === e;
                return (
                  <button
                    key={e}
                    className={`${styles.emojiBtn} ${isSelected ? styles.emojiBtnSelected : ""}`}
                    onClick={() => handleReact(e)}
                    title={isSelected ? "Tap to remove" : e}
                  >
                    {e}
                  </button>
                );
              })}
              {/* + button opens full emoji picker */}
              <button
                className={styles.emojiMore}
                title="More emojis"
                onClick={(e) => { e.stopPropagation(); setShowMoreEmojis(true); setMenuOpen(false); }}
              >
                <span>+</span>
              </button>
            </div>

            <div className={styles.menuDivider} />

            {/* Menu items */}
            <button className={styles.menuItem} onClick={handleReply}>
              <span className={styles.menuIcon}><IconReply /></span>
              <span className={styles.menuLabel}>Reply</span>
            </button>

            {message.text && (
              <button className={styles.menuItem} onClick={handleCopy}>
                <span className={styles.menuIcon}><IconCopy /></span>
                <span className={styles.menuLabel}>Copy</span>
              </button>
            )}

            <button className={styles.menuItem} onClick={handleForward}>
              <span className={styles.menuIcon}><IconForward /></span>
              <span className={styles.menuLabel}>Forward</span>
            </button>

            <button className={styles.menuItem} onClick={handlePin}>
              <span className={styles.menuIcon}><IconPin /></span>
              <span className={styles.menuLabel}>{isPinned ? "Unpin" : "Pin"}</span>
            </button>

            {isOwn && message.text && (
              <button className={styles.menuItem} onClick={handleEdit}>
                <span className={styles.menuIcon}><IconEdit /></span>
                <span className={styles.menuLabel}>Edit</span>
              </button>
            )}

            {isOwn && <div className={styles.menuDivider} />}

            {isOwn && (
              <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={handleDelete}>
                <span className={styles.menuIcon}><IconDelete /></span>
                <span className={styles.menuLabel}>Delete</span>
              </button>
            )}
          </div>
        )}
      </div>

      {showForward && <ForwardModal message={message} onClose={() => setShowForward(false)} />}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      {showMoreEmojis && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400 }} onClick={() => setShowMoreEmojis(false)}>
          <div style={{ position: "absolute", bottom: 80, left: "50%", transform: "translateX(-50%)" }}
               onClick={(e) => e.stopPropagation()}>
            <EmojiPicker
              onSelect={(emoji) => { handleReact(emoji); setShowMoreEmojis(false); }}
              onClose={() => setShowMoreEmojis(false)}
            />
          </div>
        </div>
      )}
      {memberPopup && (
        <MemberProfilePopup
          member={memberPopup.member}
          anchorRect={memberPopup.rect}
          onClose={() => setMemberPopup(null)}
        />
      )}
      {showReactionDetail && message.reactions?.length > 0 && (
        <ReactionDetailModal
          reactions={message.reactions}
          onClose={() => setShowReactionDetail(false)}
        />
      )}

      {/* ── Delete confirmation modal ── */}
      {showDeleteModal && (
        <div className={styles.deleteModalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.deleteModalBox} onClick={(e) => e.stopPropagation()}>
            <p className={styles.deleteModalTitle}>Delete Message?</p>
            <p className={styles.deleteModalSub}>This message will be deleted</p>
            <div className={styles.deleteModalBtns}>
              <button className={styles.deleteModalCancel} onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className={styles.deleteModalForMe} onClick={() => handleDeleteConfirm(false)}>
                Delete for Me
              </button>
              {isOwn && (
                <button className={styles.deleteModalForAll} onClick={() => handleDeleteConfirm(true)}>
                  Delete for Everyone
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
